/**
 * Image to Video screen.
 *
 * Two ways in, one screen:
 *  - "Use Template": pick a ready-made style, add a photo. The style's prompt
 *    lives on the server and is never shown or sent by the client.
 *  - "Customize": add a photo and describe the motion yourself.
 *
 * Credits are data (GET /credits/pricing); nothing here hard-codes a price.
 * Defaults: 5s and the Settings quality (540p unless the user picked another);
 * people buy more credits when they want more, not the other way round.
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { getPrefs } from '../state/prefs.js';
import { escapeHtml } from '../components/placeholder.js';
import { cardThumbUrl } from '../components/templateCard.js';
import { icon } from '../components/icon.js';
import { beforeInsetHtml } from '../components/beforeInset.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import { MOTION_PRESETS } from '../content/motionPresets.js';
import { SAMPLE_PHOTOS } from '../content/samplePhotos.js';
import type {
  CreditPricingConfig,
  QualityTier,
  Template,
  UploadFileMeta,
  VideoDurationSec,
} from '@loomia/shared';

const TOOL = 'image_to_video' as const;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;
const PROMPT_MAX = 500;
const PROMPT_MIN = 5;
/** AI sound comes from Veo, which makes short clips: 5 s only (server rule). */
const SOUND_DURATION: VideoDurationSec = 5;
const TIER_ORDER: QualityTier[] = ['360p', '540p', '720p', '1080p'];
const TIER_HINT: Record<QualityTier, string> = {
  '360p': 'Cheapest',
  '540p': 'Balanced',
  '720p': 'HD',
  '1080p': 'Full HD',
};

type Mode = 'template' | 'custom';

interface Photo {
  status: 'empty' | 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  error: string | null;
}

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function priceOf(
  pricing: CreditPricingConfig,
  tier: QualityTier,
  duration: VideoDurationSec,
): number | null {
  const v = pricing.perTool[TOOL]?.perTierDuration?.[tier]?.[duration];
  return typeof v === 'number' ? v : null;
}

function errorHtml(message: string): string {
  return `
    <div class="i2v-state">
      ${icon('cloud_off')}
      <p>${escapeHtml(message)}</p>
      <button class="btn btn-secondary" data-act="retry">Try again</button>
    </div>`;
}

export function renderImageToVideo(root: HTMLElement, query: URLSearchParams): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="i2v"><div class="i2v-skel"></div></div>';

    const slug = query.get('template')?.trim() || null;
    const [pricingR, listR, bySlugR] = await Promise.allSettled([
      api.getPricing(),
      api.listTemplates({ tool: TOOL, limit: 24 }),
      slug ? api.getTemplate(slug) : Promise.resolve(null),
      refreshCredits().catch(() => null),
    ]);
    if (!alive()) return;
    if (pricingR.status === 'rejected') {
      root.innerHTML = `<div class="i2v">${errorHtml("Couldn't load pricing. Check your connection.")}</div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    const pricing = pricingR.value;
    const templates = listR.status === 'fulfilled' ? listR.value.templates : [];
    let selected: Template | null = null;
    if (bySlugR.status === 'fulfilled' && bySlugR.value && bySlugR.value.tool === TOOL) {
      selected = bySlugR.value;
      if (!templates.some((t) => t.id === selected!.id)) templates.unshift(selected);
    }
    mount(pricing, templates, selected ?? templates[0] ?? null);
  };

  const mount = (
    pricing: CreditPricingConfig,
    templates: Template[],
    initial: Template | null,
  ): void => {
    const matrix = pricing.perTool[TOOL]?.perTierDuration ?? {};
    const allTiers = TIER_ORDER.filter((t) => matrix[t]);
    const durations: VideoDurationSec[] = pricing.perTool[TOOL]?.durations?.length
      ? pricing.perTool[TOOL]!.durations!
      : [5, 10, 15];
    if (allTiers.length === 0) {
      root.innerHTML = `<div class="i2v">${errorHtml('Image to Video is not available right now.')}</div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }

    const hasTemplates = templates.length > 0;
    let mode: Mode = hasTemplates ? 'template' : 'custom';
    let template: Template | null = initial;
    let photo: Photo = {
      status: 'empty',
      progress: 0,
      previewUrl: null,
      fileId: null,
      error: null,
    };
    let prompt = '';
    let presetId: string | null = null;
    let generating = false;

    const tiersFor = (): QualityTier[] => {
      if (mode === 'template' && template?.qualityTiers?.length) {
        const allowed = allTiers.filter((t) => template!.qualityTiers!.includes(t));
        if (allowed.length) return allowed;
      }
      return allTiers;
    };

    const q = (n: number): VideoDurationSec | null =>
      (durations as number[]).includes(n) ? (n as VideoDurationSec) : null;
    let duration: VideoDurationSec = q(Number(query.get('duration'))) ?? durations[0]!;
    const wantedTier = query.get('quality');
    let tier: QualityTier =
      (allTiers as string[]).includes(wantedTier ?? '')
        ? (wantedTier as QualityTier)
        : allTiers.includes(getPrefs().quality)
          ? getPrefs().quality
          : allTiers[0]!;
    const fixTier = (): void => {
      const t = tiersFor();
      if (!t.includes(tier)) tier = t[0]!;
    };
    fixTier();

    let sound = false;
    const surcharge = pricing.audioSurcharge ?? 20;
    const soundOn = (): boolean => sound && duration === SOUND_DURATION;
    const cost = (): number => (priceOf(pricing, tier, duration) ?? 0) + (soundOn() ? surcharge : 0);
    const promptOk = (): boolean => prompt.trim().length >= PROMPT_MIN;
    const missing = (): string | null => {
      if (mode === 'template' && !template) return 'Choose a style first';
      if (photo.status === 'uploading') return 'Uploading…';
      if (photo.status !== 'ready') return 'Add your photo first';
      if (mode === 'custom' && !promptOk()) return 'Describe the motion first';
      return null;
    };

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string, extra = ''): string =>
      `<div class="i2v-step"><span class="i2v-num">${n}</span><h2>${title}</h2>${extra}</div>`;

    const heroHtml = (): string => {
      if (!template) return '';
      const vid = template.previewVideoUrl;
      return `
        <div class="i2v-hero">
          ${
            vid
              ? `<video src="${escapeHtml(vid)}" poster="${escapeHtml(template.previewUrl)}" autoplay muted loop playsinline></video>`
              : `<img src="${escapeHtml(template.previewUrl)}" alt="" />`
          }
          ${template.beforeUrl ? beforeInsetHtml(template.beforeUrl, 'is-small') : ''}
          <div class="i2v-hero-text">
            <span class="i2v-hero-kicker">Selected style</span>
            <strong>${escapeHtml(template.title)}</strong>
          </div>
        </div>`;
    };

    const railHtml = (): string => `
      <div class="i2v-rail" role="listbox" aria-label="Video styles">
        ${templates
          .map(
            (t) => `
          <button class="i2v-tpl${t.id === template?.id ? ' selected' : ''}" role="option"
            aria-selected="${t.id === template?.id}" data-tpl="${escapeHtml(t.id)}">
            <img src="${escapeHtml(cardThumbUrl(t.thumbnailUrl))}" alt="" loading="lazy" decoding="async" />
            <span>${escapeHtml(t.title)}</span>
          </button>`,
          )
          .join('')}
      </div>`;

    const photoHtml = (): string => {
      if (photo.status === 'empty') {
        return `
          <button class="i2v-drop" data-act="pick">
            ${icon('add_photo_alternate')}
            <strong>Add your photo</strong>
            <span>JPG, PNG or WebP · up to 25 MB</span>
          </button>`;
      }
      const label =
        photo.status === 'uploading'
          ? `Uploading… ${Math.round(photo.progress * 100)}%`
          : photo.status === 'error'
            ? escapeHtml(photo.error ?? 'Upload failed')
            : 'Photo ready';
      return `
        <div class="i2v-photo is-${photo.status}">
          ${photo.previewUrl ? `<img src="${escapeHtml(photo.previewUrl)}" alt="Your photo" />` : ''}
          ${photo.status === 'uploading' ? `<div class="i2v-bar"><span style="width:${Math.round(photo.progress * 100)}%"></span></div>` : ''}
          <div class="i2v-photo-bar">
            <span class="i2v-photo-label" data-photo-label>${label}</span>
            ${photo.status === 'error' ? '<button class="i2v-link" data-act="pick">Try again</button>' : ''}
            <button class="i2v-link" data-act="pick">Change</button>
            <button class="i2v-link" data-act="remove">Remove</button>
          </div>
        </div>`;
    };

    const samplesHtml = (): string =>
      SAMPLE_PHOTOS.length === 0 || photo.status !== 'empty'
        ? ''
        : `
        <div class="i2v-samples">
          <p class="i2v-sub">Try these photos</p>
          <div class="i2v-rail">
            ${SAMPLE_PHOTOS.map(
              (s, i) => `<button class="i2v-sample" data-sample="${i}" aria-label="${escapeHtml(s.label)}"><img src="${escapeHtml(s.url)}" alt="" loading="lazy" /></button>`,
            ).join('')}
          </div>
        </div>`;

    const customHtml = (): string => `
      <div class="i2v-chips" role="group" aria-label="Motion presets">
        ${MOTION_PRESETS.map(
          (p) => `<button class="i2v-pchip${p.id === presetId ? ' selected' : ''}" data-preset="${p.id}">${escapeHtml(p.label)}</button>`,
        ).join('')}
      </div>
      <div class="i2v-text">
        <textarea id="i2v-prompt" maxlength="${PROMPT_MAX}" rows="4"
          placeholder="Describe how your photo should move…" aria-label="Describe the motion"></textarea>
        <div class="i2v-text-foot">
          <button class="i2v-link" data-act="clear" id="i2v-clear" hidden>Clear</button>
          <span class="i2v-count" id="i2v-count">0/${PROMPT_MAX}</span>
        </div>
      </div>`;

    const durationHtml = (): string => `
      <div class="i2v-opts" role="radiogroup" aria-label="Duration">
        ${durations
          .map(
            (d) => `
          <button class="i2v-opt${d === duration ? ' selected' : ''}" role="radio" aria-checked="${d === duration}" data-duration="${d}">
            <strong>${d}s</strong><span>${icon('toll', { fill: true })} ${priceOf(pricing, tier, d) ?? '–'}</span>
          </button>`,
          )
          .join('')}
      </div>`;

    const qualityHtml = (): string => `
      <div class="i2v-opts" role="radiogroup" aria-label="Quality">
        ${tiersFor()
          .map(
            (t) => `
          <button class="i2v-opt${t === tier ? ' selected' : ''}" role="radio" aria-checked="${t === tier}" data-tier="${t}">
            <strong>${t}</strong><small>${TIER_HINT[t]}</small><span>${icon('toll', { fill: true })} ${priceOf(pricing, t, duration) ?? '–'}</span>
          </button>`,
          )
          .join('')}
      </div>
      <p class="i2v-sub">Higher quality = sharper video</p>`;

    const soundHtml = (): string => {
      const can = duration === SOUND_DURATION;
      return `
      <button class="i2v-sound${soundOn() ? ' selected' : ''}" role="switch" aria-checked="${soundOn()}" data-act="sound" ${can ? '' : 'disabled'}>
        <span class="i2v-sound-text"><strong>AI sound</strong><small>${
          can ? 'Music and effects that match the scene · vertical 9:16 video' : `Available for ${SOUND_DURATION}s clips only`
        }</small></span>
        <span class="i2v-sound-price">${icon('toll', { fill: true })} +${surcharge}</span>
        <span class="i2v-switch" aria-hidden="true"></span>
      </button>`;
    };

    const footerHtml = (): string => {
      const c = cost();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      let btn: string;
      if (short > 0 && !why) {
        btn = '<button class="btn btn-primary btn-block" data-act="buy">Get more credits</button>';
      } else {
        btn = `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
          generating ? 'Starting…' : why ? escapeHtml(why) : `Generate Video ${icon('toll', { fill: true })} ${c}`
        }</button>`;
      }
      return `
        <div class="i2v-total${short > 0 ? ' is-short' : ''}">
          <span>Total</span><strong>${icon('toll', { fill: true })} ${c}</strong>
        </div>
        ${short > 0 ? `<p class="i2v-warn">You need ${icon('toll', { fill: true })} ${short} more credits</p>` : ''}
        ${btn}
        <p class="i2v-refund">Failed runs are refunded automatically</p>`;
    };

    const paint = (): void => {
      fixTier();
      const t = mode === 'template';
      let n = 0;
      root.innerHTML = `
        <div class="i2v">
          <header class="i2v-head">
            <button class="i2v-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <h1>Image to Video</h1>
            <a class="i2v-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          ${
            hasTemplates
              ? `<div class="i2v-toggle" role="tablist">
                  <button role="tab" aria-selected="${t}" class="${t ? 'selected' : ''}" data-mode="template">Use Template</button>
                  <button role="tab" aria-selected="${!t}" class="${!t ? 'selected' : ''}" data-mode="custom">Customize</button>
                </div>`
              : ''
          }
          ${t ? `${step(++n, 'Choose a style', '<a class="i2v-link i2v-all" href="#/explore">See all</a>')}${heroHtml()}${railHtml()}` : ''}
          ${step(++n, 'Upload photo')}
          ${photoHtml()}
          ${samplesHtml()}
          ${t ? '' : `${step(++n, 'Describe the motion')}${customHtml()}`}
          ${step(++n, 'Duration')}
          ${durationHtml()}
          ${step(++n, 'Quality')}
          ${qualityHtml()}
          ${step(++n, 'Sound')}
          ${soundHtml()}
          <footer class="i2v-foot" id="i2v-foot">${footerHtml()}</footer>
          <input type="file" id="i2v-file" accept="${ACCEPTED_TYPES.join(',')}" hidden />
        </div>`;
      if (!t) {
        const ta = root.querySelector<HTMLTextAreaElement>('#i2v-prompt');
        if (ta) ta.value = prompt;
        syncPromptUi();
      }
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#i2v-foot');
      if (f) f.innerHTML = footerHtml();
    };

    function syncPromptUi(): void {
      const count = root.querySelector<HTMLElement>('#i2v-count');
      if (count) count.textContent = `${prompt.length}/${PROMPT_MAX}`;
      const clear = root.querySelector<HTMLElement>('#i2v-clear');
      if (clear) clear.hidden = prompt.length === 0;
      root.querySelectorAll<HTMLElement>('[data-preset]').forEach((b) => {
        b.classList.toggle('selected', b.dataset.preset === presetId);
      });
    }

    // -- Upload -----------------------------------------------------------

    const upload = async (file: File): Promise<void> => {
      if (!requireSignIn('Sign in to add your photo.')) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > CLIENT_MAX_BYTES) {
        toast('That image is too large. Please pick one under 25 MB.');
        return;
      }
      const old = photo;
      if (old.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(old.previewUrl);
      photo = {
        status: 'uploading',
        progress: 0,
        previewUrl: URL.createObjectURL(file),
        fileId: null,
        error: null,
      };
      const mine = photo;
      paint();
      try {
        const req = await api.requestUpload({
          purpose: 'generation_input',
          contentType: file.type,
          contentLength: file.size,
        });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || photo !== mine) return;
          mine.progress = r;
          const bar = root.querySelector<HTMLElement>('.i2v-bar span');
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
          const label = root.querySelector<HTMLElement>('[data-photo-label]');
          if (label) label.textContent = `Uploading… ${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || photo !== mine) return;
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        mine.status = 'ready';
        mine.fileId = meta.id;
        mine.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? mine.previewUrl;
      } catch (err) {
        if (!alive() || photo !== mine) return;
        mine.status = 'error';
        mine.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
      }
      paint();
    };

    const removePhoto = (): void => {
      const oldId = photo.fileId;
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
      photo = { status: 'empty', progress: 0, previewUrl: null, fileId: null, error: null };
      paint();
      if (oldId) void api.deleteUpload(oldId).catch(() => undefined);
    };

    // -- Generate ---------------------------------------------------------

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = cost();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const { job } = await api.submitJob({
          tool: TOOL,
          // Template mode sends only the id: the prompt is composed server-side.
          ...(mode === 'template' && template ? { templateId: template.id } : {}),
          ...(mode === 'custom' ? { prompt: prompt.trim() } : {}),
          inputFileIds: photo.fileId ? [photo.fileId] : [],
          qualityTier: tier,
          durationSec: duration,
          ...(soundOn() ? { options: { audio: true } } : {}),
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated; survives repaints) ------------------------------

    root.onclick = (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>(
        '[data-act],[data-mode],[data-tpl],[data-preset],[data-duration],[data-tier],[data-sample]',
      );
      if (!el) return;
      const d = el.dataset;
      if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'pick') {
        if (requireSignIn('Sign in to add your photo.')) {
          withUploadGuide('image_to_video', () => root.querySelector<HTMLInputElement>('#i2v-file')?.click());
        }
      } else if (d.act === 'remove') removePhoto();
      else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.act === 'sound') {
        sound = !sound;
        paint();
      }
      else if (d.act === 'clear') {
        prompt = '';
        presetId = null;
        const ta = root.querySelector<HTMLTextAreaElement>('#i2v-prompt');
        if (ta) {
          ta.value = '';
          ta.focus();
        }
        syncPromptUi();
        paintFooter();
      } else if (d.mode) {
        mode = d.mode as Mode;
        paint();
      } else if (d.tpl) {
        template = templates.find((t) => t.id === d.tpl) ?? template;
        paint();
      } else if (d.preset) {
        const p = MOTION_PRESETS.find((x) => x.id === d.preset);
        if (p) {
          presetId = p.id;
          prompt = p.text;
          const ta = root.querySelector<HTMLTextAreaElement>('#i2v-prompt');
          if (ta) ta.value = prompt;
          syncPromptUi();
          paintFooter();
        }
      } else if (d.duration) {
        duration = Number(d.duration) as VideoDurationSec;
        paint();
      } else if (d.tier) {
        tier = d.tier as QualityTier;
        paint();
      } else if (d.sample) {
        const s = SAMPLE_PHOTOS[Number(d.sample)];
        if (s) {
          void fetch(s.url)
            .then((r) => r.blob())
            .then((b) => upload(new File([b], 'sample.webp', { type: b.type || 'image/webp' })))
            .catch(() => toast("Couldn't load that sample photo."));
        }
      }
    };

    root.oninput = (e) => {
      const ta = e.target as HTMLElement;
      if (ta.id !== 'i2v-prompt') return;
      prompt = (ta as HTMLTextAreaElement).value;
      // Editing a preset's text makes it the user's own prompt.
      const p = MOTION_PRESETS.find((x) => x.id === presetId);
      if (p && p.text !== prompt) presetId = null;
      syncPromptUi();
      paintFooter();
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'i2v-file') return;
      const f = input.files?.[0];
      input.value = '';
      if (f) void upload(f);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    prevCleanup = () => {
      unsub();
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
    };

    paint();
  };

  void load();
}
